import { AvatarContentTypeSchema } from "@iec-id/shared/contracts/profile";
import { AvatarPicker as AvatarPickerControl } from "@iec-id/ui";
import { type ChangeEvent, useRef, useState } from "react";
import {
	AVATAR_MAX_SOURCE_BYTES,
	AvatarPrepareError,
	prepareAvatarJpeg,
} from "@/lib/prepare-avatar";
import { api } from "@/lib/trpc";
import { useOnboardingT } from "./onboarding-i18n";

export interface AvatarPickerProps {
	/** Инициалы — фолбэк на СЕТЕВУЮ ОШИБКУ, а не на «фото нет». */
	initials: string;
	/** Картинка человека: своё фото либо наша заглушка. Пустой не бывает. */
	initialUrl: string | null;
	/** Загружено ли своё фото — от этого зависит подпись кнопки. */
	hasCustomAvatar: boolean;
	/** Ключ загруженного объекта уходит вместе с шагом `identity`. */
	onUploaded: (avatarKey: string) => void;
}

/**
 * Аватар 72px и кнопка «Добавить фото» (макет 4b-1).
 *
 * Исходник кропается в квадрат 512 JPEG на клиенте — тот же кадр, что кладёт
 * витрина. В S3 уходит уже сжатый файл: presign смотрит на заявленный размер,
 * и сырой 8 МБ фото сервер отвергал бы ещё до PUT. Лимит исходника — 12 МБ,
 * итог сверяем с `maxSizeBytes` из билета (канон сервера — 5 МБ).
 */
export function AvatarPicker({
	initials,
	initialUrl,
	hasCustomAvatar,
	onUploaded,
}: AvatarPickerProps) {
	const t = useOnboardingT();
	const inputRef = useRef<HTMLInputElement>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(initialUrl);
	/**
	 * Своё фото есть — либо оно уже было, либо человек загрузил его сейчас.
	 * По `previewUrl` этот вопрос не решается: там всегда стоит картинка (наша
	 * заглушка тоже картинка), и кнопка звала «Заменить фото» того, кто ничего
	 * не загружал.
	 */
	const [ownPhoto, setOwnPhoto] = useState(hasCustomAvatar);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	const requestUpload = api.profile.uploadAvatarUrl.useMutation();

	async function handleFile(event: ChangeEvent<HTMLInputElement>) {
		const file = event.target.files?.[0];
		// Сброс значения — иначе повторный выбор того же файла не даёт события.
		event.target.value = "";
		if (!file) return;

		if (!AvatarContentTypeSchema.safeParse(file.type).success) {
			setError(t("identity.photoWrongType"));
			return;
		}

		setError(null);
		setBusy(true);
		try {
			const blob = await prepareAvatarJpeg(file);
			const ticket = await requestUpload.mutateAsync({
				contentType: "image/jpeg",
				sizeBytes: blob.size,
			});

			if (blob.size > ticket.maxSizeBytes) {
				const limit = Math.floor(ticket.maxSizeBytes / (1024 * 1024));
				setError(t("identity.photoTooLarge", { limit }));
				return;
			}

			const response = await fetch(ticket.uploadUrl, {
				method: "PUT",
				body: blob,
				headers: { "Content-Type": "image/jpeg" },
			});
			if (!response.ok) throw new Error(`S3 PUT ${response.status}`);

			setPreviewUrl((previous) => {
				if (previous?.startsWith("blob:")) URL.revokeObjectURL(previous);
				return URL.createObjectURL(blob);
			});
			setOwnPhoto(true);
			onUploaded(ticket.avatarKey);
		} catch (cause) {
			if (cause instanceof AvatarPrepareError && cause.reason === "size") {
				setError(
					t("identity.photoTooLarge", {
						limit: Math.floor(AVATAR_MAX_SOURCE_BYTES / (1024 * 1024)),
					}),
				);
				return;
			}
			if (cause instanceof AvatarPrepareError && cause.reason === "decode") {
				setError(t("identity.photoFailed"));
				return;
			}
			setError(t("identity.photoFailed"));
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-4">
				{/* Знак камеры прямо на портрете — так же, как в витрине: две
				    поверхности, где меняют одно и то же фото, обязаны делать это
				    одинаково. */}
				<AvatarPickerControl
					size={72}
					initials={initials}
					src={previewUrl ?? undefined}
					alt={t("identity.avatarAlt")}
					// Картинка здесь гарантирована: `/api/avatar/{userId}` отдаёт фото
					// либо рисует заглушку сам. Инициалы поверх неё — второе «нет
					// фото» в той же экосистеме, поэтому фолбэк ждёт полторы секунды
					// и остаётся реакцией на сетевую ошибку, а не состоянием загрузки.
					fallbackDelayMs={1500}
					label={ownPhoto ? t("identity.changePhoto") : t("identity.addPhoto")}
					disabled={busy}
					onSelect={() => inputRef.current?.click()}
				/>
				{busy ? <span className="t-meta text-fg-muted">{t("identity.uploading")}</span> : null}
				<input
					ref={inputRef}
					type="file"
					accept="image/jpeg,image/png,image/webp"
					className="sr-only"
					aria-label={t("identity.addPhoto")}
					onChange={(event) => void handleFile(event)}
				/>
			</div>
			<p className="t-meta text-fg-muted">{t("identity.photoHint")}</p>
			{error ? (
				<span role="alert" className="t-meta text-danger">
					{error}
				</span>
			) : null}
		</div>
	);
}
